import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip as RTooltip,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Tooltip as RadixTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Info } from "lucide-react";

interface SensitivityMiniProps {
  baselineIRR: number; // в долях, например 0.18 = 18%
  deltaPercent?: number;
  // Реальные данные для расчёта чувствительности
  totalInvestment: number;
  ebitdaByYear: number[];
  netSaleProceeds: number;
  exitYear: number;
  adrByYear: number[];
  occupancyByYear: number[];
}

interface CustomTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{ value: number; name: string; color?: string }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
      <p className="font-semibold text-slate-900">{label}</p>
      {payload.map((entry, index) => (
        <p
          key={index}
          className="text-sm"
          style={{ color: entry.color || "#64748b" }}
        >
          {entry.name}: {entry.value >= 0 ? "+" : ""}
          {entry.value.toFixed(1)}pp
        </p>
      ))}
    </div>
  );
};

// Простой расчёт IRR методом бисекции
function calcIRR(cashflows: number[]): number {
  let lo = -0.999,
    hi = 10,
    mid = 0;
  for (let i = 0; i < 100; i++) {
    mid = (lo + hi) / 2;
    const npv = cashflows.reduce(
      (sum, cf, t) => sum + cf / Math.pow(1 + mid, t),
      0,
    );
    if (Math.abs(npv) < 0.01) break;
    if (npv > 0) lo = mid;
    else hi = mid;
  }
  return mid;
}

function buildCashflows(
  investment: number,
  ebitdaByYear: number[],
  netSaleProceeds: number,
  exitYear: number,
  ebitdaMultiplier = 1,
  investmentMultiplier = 1,
  saleProceedsMultiplier = 1,
): number[] {
  const cfs: number[] = [-investment * investmentMultiplier];
  for (let y = 1; y <= exitYear; y++) {
    const ebitda = (ebitdaByYear[y - 1] || 0) * ebitdaMultiplier;
    const exit = y === exitYear ? netSaleProceeds * saleProceedsMultiplier : 0;
    cfs.push(ebitda + exit);
  }
  return cfs;
}

export default function SensitivityMini({
  baselineIRR,
  deltaPercent = 10,
  totalInvestment,
  ebitdaByYear,
  netSaleProceeds,
  exitYear,
  adrByYear,
  occupancyByYear,
}: SensitivityMiniProps) {
  const delta = deltaPercent / 100;

  // Базовые кэшфлоу
  const baseCFs = buildCashflows(
    totalInvestment,
    ebitdaByYear,
    netSaleProceeds,
    exitYear,
  );
  const baseIRR = calcIRR(baseCFs) * 100; // в процентах

  // ADR +/- deltaPercent → EBITDA меняется пропорционально выручке (~60% маржа)
  const adrUpEbitda = ebitdaByYear.map((e) => e * (1 + delta * 0.6));
  const adrDownEbitda = ebitdaByYear.map((e) => e * (1 - delta * 0.6));

  // Occupancy +/- → аналогично
  const occUpEbitda = ebitdaByYear.map((e) => e * (1 + delta * 0.5));
  const occDownEbitda = ebitdaByYear.map((e) => e * (1 - delta * 0.5));

  // Exit cap rate +/- → меняет sale proceeds (~inversely)
  const capRateUpProceeds = netSaleProceeds * (1 - delta); // выше ставка = ниже стоимость
  const capRateDownProceeds = netSaleProceeds * (1 + delta);

  // CapEx +/- → меняет investment
  const capexUpIRR =
    calcIRR(
      buildCashflows(
        totalInvestment,
        ebitdaByYear,
        netSaleProceeds,
        exitYear,
        1,
        1 + delta,
      ),
    ) * 100;
  const capexDownIRR =
    calcIRR(
      buildCashflows(
        totalInvestment,
        ebitdaByYear,
        netSaleProceeds,
        exitYear,
        1,
        1 - delta,
      ),
    ) * 100;

  // OpEx +/- → меняет ebitda (~30% от выручки это опекс)
  const opexUpEbitda = ebitdaByYear.map((e) => e * (1 - delta * 0.4));
  const opexDownEbitda = ebitdaByYear.map((e) => e * (1 + delta * 0.4));

  const sensitivityData = [
    {
      name: "ADR",
      upside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            adrUpEbitda,
            netSaleProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
      downside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            adrDownEbitda,
            netSaleProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
    },
    {
      name: "Occupancy",
      upside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            occUpEbitda,
            netSaleProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
      downside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            occDownEbitda,
            netSaleProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
    },
    {
      name: "Exit Cap Rate",
      upside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            ebitdaByYear,
            capRateDownProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
      downside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            ebitdaByYear,
            capRateUpProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
    },
    {
      name: "CapEx",
      upside: capexDownIRR - baseIRR,
      downside: capexUpIRR - baseIRR,
    },
    {
      name: "Operating Costs",
      upside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            opexDownEbitda,
            netSaleProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
      downside:
        calcIRR(
          buildCashflows(
            totalInvestment,
            opexUpEbitda,
            netSaleProceeds,
            exitYear,
          ),
        ) *
          100 -
        baseIRR,
    },
  ];

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <CardTitle className="text-lg">Sensitivity Analysis</CardTitle>
          <TooltipProvider>
            <RadixTooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">
                  Shows how much your IRR would move if key assumptions change
                  by ±{deltaPercent}%. Longer bars = bigger impact on returns.
                </p>
              </TooltipContent>
            </RadixTooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-slate-500">
          Baseline IRR: {baseIRR.toFixed(1)}% — impact of ±{deltaPercent}%
          changes
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={sensitivityData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 12 }}
                tickFormatter={(v) => `${v >= 0 ? "+" : ""}${v.toFixed(1)}pp`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12 }}
                width={100}
              />
              <ReferenceLine x={0} stroke="#94a3b8" strokeDasharray="2 2" />
              <RTooltip content={<CustomTooltip />} />
              <Bar dataKey="downside" fill="#ef4444" name="Downside" />
              <Bar dataKey="upside" fill="#10b981" name="Upside" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
          <strong>Note:</strong> Sensitivity shows potential IRR impact from ±
          {deltaPercent}% assumption changes. Results are estimates based on
          current model structure.
        </div>
      </CardContent>
    </Card>
  );
}
